import React from 'react';

import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';

import { stripOpenAiTracking } from './aiReviewReason';

export interface IAiReviewFinding {
  code: string;
  severity: string;
  legalBasis: string | null;
}

// The finding codes of the AI review (b2bservices rest/ai/AiReview/findings.ts) in words.
export const FINDING_LABELS: Record<string, string> = {
  MISSING_REQUIRED: 'Required field missing',
  SPELLING: 'Spelling',
  SEMANTIC_MISMATCH: 'Value does not fit the field',
  NET_QUANTITY: 'Net quantity',
  QUANTITY_INCONSISTENT: 'Quantities do not match',
  IDENTITY_CONFLICT: 'Product identity conflict',
  MISLEADING_TEXT: 'Misleading text',
  INGREDIENT_ORDER: 'Ingredient order',
  QUID_MISSING: 'QUID missing',
  ALLERGEN_NOT_EMPHASISED: 'Allergen not emphasised',
  ALLERGEN_REFERENCE_MISSING: 'Allergen reference missing',
  INGREDIENT_NAME: 'Ingredient name',
  ADDITIVE_DECLARATION: 'Additive declaration',
  FORMATTING: 'Formatting',
  NUTRITION_INCONSISTENT: 'Nutrition values do not add up',
  NUTRITION_BASE: 'Nutrition base',
  STORAGE_INSTRUCTIONS: 'Storage instructions',
  ORIGIN: 'Origin',
  PRODUCT_SPECIFIC_INFO: 'Product-specific information',
  DEPOSIT: 'Deposit',
  WARNING_MISSING: 'Mandatory warning',
  FREE_FROM_CONTRADICTION: '"Free from" contradicted',
  NUTRITION_CLAIM: 'Nutrition claim',
  HEALTH_CLAIM: 'Health claim',
  PROPERTY_CLAIM: 'Property or effect claim',
  SUPPLEMENT_DOSAGE: 'Supplement dosage',
  COSMETIC_INGREDIENT: 'Cosmetic ingredient',
  HAZARD_LABELLING: 'Hazard labelling',
  CE_MARKING: 'CE marking',
  USAGE_INSTRUCTIONS: 'Usage instructions',
  LABEL_CLAIM: 'Label or characteristic',
  OTHER: 'Other',
};

// What each code means and when the AI review gives it, with an example, for the tooltips.
export const FINDING_DESCRIPTIONS: Record<string, string> = {
  MISSING_REQUIRED: 'A field that is mandatory for this product type is empty, e.g. no product description or no ingredient list for a food.',
  SPELLING: 'An obvious typo in text the producer wrote, such as a place, species or common Swedish word ("Sveirge" for "Sverige", "veg" for "vegetabiliska").',
  SEMANTIC_MISMATCH: 'The value does not mean what the field asks for, e.g. a region instead of a country of origin, or snus nicotine fields filled for an e-cigarette.',
  NET_QUANTITY: 'The net quantity or its unit is wrong, unlikely or incomplete for the product, e.g. ice cream given only in millilitres where a weight is also expected.',
  QUANTITY_INCONSISTENT: 'Quantity fields or texts contradict each other, e.g. 250 g in the net quantity field but 500 g in the description, or a pack count that does not add up.',
  IDENTITY_CONFLICT: 'Title, category and description disagree on what the product is, e.g. the category says white snus while the title and text describe an e-cigarette kit.',
  MISLEADING_TEXT: 'A text could mislead the consumer or is irrelevant to the product, without being a nutrition or health claim.',
  INGREDIENT_ORDER: 'Ingredients are not listed in descending order of weight, e.g. an ingredient that must weigh more stands after a lighter one.',
  QUID_MISSING: 'The percentage of an ingredient is missing although the law requires it: it is in the product name, emphasised in words or pictures, or usually associated with the name (EU 1169/2011 Art. 22). E.g. no % hazelnut in "Hazelnut ice cream".',
  ALLERGEN_NOT_EMPHASISED: 'One of the 14 allergens is in the ingredient list but not emphasised; on the platform emphasis is written in CAPITALS (EU 1169/2011 Art. 21), e.g. "mjölk" instead of "MJÖLK".',
  ALLERGEN_REFERENCE_MISSING: 'An ingredient whose name does not clearly show the allergen lacks a reference to it, e.g. "vassle" without "(MJÖLK)".',
  INGREDIENT_NAME: 'An ingredient lacks its legal or generic name, or the name does not match what it contains, e.g. "frukt" without the fruits listed, or a compound ingredient whose parts contradict its name.',
  ADDITIVE_DECLARATION: 'An additive or enzyme lacks its category name before the name or E number, or uses a name that is not an official category, e.g. "E211" alone or "konsistensgivare" for glycerol.',
  FORMATTING: 'A formatting mistake in a list or text, such as a comma before a closing bracket or capitals on words that are not allergens.',
  NUTRITION_INCONSISTENT: 'Nutrition values do not add up: fat, carbohydrate, protein, fibre and salt over 100 g per 100 g, sugars above carbohydrate, saturates above fat, or energy far from the macronutrients.',
  NUTRITION_BASE: 'Nutrition values are given on the wrong base, e.g. per 100 g for a drink that must use per 100 ml.',
  STORAGE_INSTRUCTIONS: 'Storage instructions are missing or not enough, e.g. no temperature for a chilled or frozen product.',
  ORIGIN: 'Origin is missing where it is mandatory (meat, fish, eggs, fruit and vegetables, honey, olive oil) or gives a region instead of a country.',
  PRODUCT_SPECIFIC_INFO: 'Information that only some products need is missing, e.g. catch method and area for wild fish, egg class, class for fruit, or alcohol content for drinks.',
  DEPOSIT: 'The deposit (pant) or packaging type of a drink does not match, e.g. the deposit amount does not fit the bottle type and volume.',
  WARNING_MISSING: 'A legal warning or mandatory statement is missing or incomplete, e.g. the caffeine warning, the AZO colour warning or the warnings a food supplement must carry.',
  FREE_FROM_CONTRADICTION: 'A "free from" or "without" claim is contradicted by the ingredients, e.g. "oil free" with butter or "fragrance free" with Parfum.',
  NUTRITION_CLAIM: 'A nutrition claim does not meet its conditions (EU 1924/2006), e.g. "high protein" when less than 20 % of the energy comes from protein.',
  HEALTH_CLAIM: 'A health claim is not allowed or lacks a related authorised specific claim, e.g. a general health message with no approved claim beside it.',
  PROPERTY_CLAIM: 'A stated property or effect is not supported, or is contradicted, by the product data, e.g. "vegan" with beeswax, or an effect claim that needs evidence not in the data.',
  SUPPLEMENT_DOSAGE: 'A food supplement problem with the daily dose, the upper intake levels or the declared amounts of vitamins, minerals and other substances.',
  COSMETIC_INGREDIENT: 'A cosmetic ingredient name does not match the EU INCI glossary, or a substance breaks a restriction for that use and amount (EC 1223/2009).',
  HAZARD_LABELLING: 'Signal word, hazard pictograms, H statements or P statements are missing, wrong or incomplete for each other (CLP, EC 1272/2008), e.g. no P statement for H301.',
  CE_MARKING: 'CE marking is missing for a product that needs it, such as toys or electronics.',
  USAGE_INSTRUCTIONS: 'Instructions for use or precautions are missing or not enough for the product to be used safely.',
  LABEL_CLAIM: 'A chosen label or characteristic (such as vegan, organic or sensitive skin) does not match the product data, or needs a check.',
  OTHER: 'A problem that fits none of the codes. Many of these suggest the code list needs a new code.',
};

export const SEVERITY_LABELS: Record<string, string> = {
  legal: 'Legal requirement',
  data_quality: 'Data quality',
  recommendation: 'Recommendation',
};

const SEVERITY_COLORS: Record<string, string> = {
  legal: '#c62828',
  data_quality: '#ef6c00',
  recommendation: '#546e7a',
};

export const findingLabel = (code: string) => FINDING_LABELS[code] || code;

const CITATION = /\s*\(\[[^\]\r\n]+\]\([^)\s\r\n]+\)\)/g;

export interface IReasonLine {
  text: string;
  // A "•" line or a "Rekommendation:" part; other lines, like a lead sentence, are not points.
  point: boolean;
}

// The lines of a reason, with each "Rekommendation:" part on a line of its own. Citations are kept,
// so the lines can be shown with their source chips.
export const reasonLines = (reason?: string | null): IReasonLine[] => {
  if (!reason) return [];
  const lines: IReasonLine[] = [];
  stripOpenAiTracking(reason).split(/\n/).forEach(line => {
    const [main, ...recommendations] = line.split(/Rekommendation:/);
    if (/^\s*•/.test(main)) lines.push({ text: main.replace(/^\s*•\s*/, '').trim(), point: true });
    else if (main.trim()) lines.push({ text: main.trim(), point: false });
    recommendations.forEach(part => lines.push({ text: `Rekommendation: ${part.trim()}`, point: true }));
  });
  return lines;
};

// The points of a reason in order: each "•" line, then each "Rekommendation:" part. The prompt asks
// for one finding per point in this order, so the n-th finding belongs to the n-th point.
export const reasonPoints = (reason?: string | null): string[] =>
  reasonLines(reason)
    .filter(line => line.point)
    .map(line => line.text.replace(CITATION, '').trim());

interface IFindingChipProps {
  finding: IAiReviewFinding;
  label?: string;
  // The point of the reason the finding is about, for the tooltip.
  point?: string;
}

// One finding as a chip; the tooltip has the code, severity, legal basis, the point and what the code means.
export const FindingChip = ({ finding, label, point }: IFindingChipProps) => (
  <Tooltip
    arrow
    enterTouchDelay={0}
    title={
      <div>
        <div>
          <strong>{finding.code}</strong> · {SEVERITY_LABELS[finding.severity] || finding.severity}
        </div>
        {finding.legalBasis && <div>{finding.legalBasis}</div>}
        {point && (
          <div style={{ marginTop: 4 }} data-testid="ai-review-finding-point">
            <em>About:</em> {point}
          </div>
        )}
        {FINDING_DESCRIPTIONS[finding.code] && <div style={{ marginTop: 4 }}>{FINDING_DESCRIPTIONS[finding.code]}</div>}
      </div>
    }
  >
    <Chip
      size="small"
      variant="outlined"
      label={label || findingLabel(finding.code)}
      style={{ borderColor: SEVERITY_COLORS[finding.severity], color: SEVERITY_COLORS[finding.severity], height: 22 }}
      data-testid="ai-review-finding"
    />
  </Tooltip>
);

// The structured findings of a field verdict as chips; the tooltip has the point of the reason the
// finding is about when the points and the findings match one to one.
const AiReviewFindings = ({ findings, reason }: { findings?: IAiReviewFinding[] | null; reason?: string | null }) => {
  if (!findings?.length) return null;
  const points = reasonPoints(reason);
  const pointOf = (index: number) => (points.length === findings.length ? points[index] : undefined);
  // Several findings with one code in a field are numbered, so their chips can be told apart.
  const sameCode = (code: string) => findings.filter(finding => finding.code === code).length;
  const numberOf = (index: number) => findings.slice(0, index + 1).filter(finding => finding.code === findings[index].code).length;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }} data-testid="ai-review-findings">
      {findings.map((finding, index) => (
        <FindingChip
          key={`${finding.code}-${index}`}
          finding={finding}
          point={pointOf(index)}
          label={sameCode(finding.code) > 1 ? `${findingLabel(finding.code)} ${numberOf(index)}` : undefined}
        />
      ))}
    </span>
  );
};

export default AiReviewFindings;
